import {createContext, MutableRefObject, ReactElement, useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import VolumeMeter from "../helper/VolumeMeter";
import {BackgroundFilterMode} from "../helper/BackgroundFilter";
import NotificationContext from "./NotificationContext";
import useWebRtcClient from "../webrtc/WebRtcClient";
import AppContext from "./AppContext";
import {enqueue} from "./media/captureChain";
import {isSystemDenial, stripLabel} from "./media/deviceLabels";
import {
  CAPTURE_COALESCE_MS, DEVICE_SETTLE_MS, DEVICE_TOAST_TTL_MS, FRESH_LABEL_MS, MIN_LABEL_VISIBLE_MS,
  RECAPTURE_MAX_ATTEMPTS, RECAPTURE_STABLE_MS, RETRY_LABEL_MS,
} from "./media/timings";
import useDeviceList from "./media/useDeviceList";
import usePublish from "./media/usePublish";
import useBackgroundFilter from "./media/useBackgroundFilter";
import useSpeakerOutput from "./media/useSpeakerOutput";
import useMicrophoneTest, {MicTestState} from "./media/useMicrophoneTest";
import useMicLevel from "./media/useMicLevel";

export type {MicTestState} from "./media/useMicrophoneTest";
export {micTestLabelKey, micTestStatusKey} from "./media/useMicrophoneTest";

type Props = {
  children: ReactElement
}

type DevicePermissions = { microphone: boolean, camera: boolean }

const MediaContext = createContext<{
  stream: MediaStream,
  videoStream: MediaStream,
  videoEnabled: boolean, setVideoEnabled: (enabled: boolean) => void
  audioEnabled: boolean, setAudioEnabled: (enabled: boolean) => void
  cameras: MediaDeviceInfo[], setCameras: (cameras: MediaDeviceInfo[]) => void
  microphones: MediaDeviceInfo[], setMicrophones: (microphones: MediaDeviceInfo[]) => void
  speakers: MediaDeviceInfo[], setSpeakers: (speakers: MediaDeviceInfo[]) => void
  selectedCamera: string|null, setSelectedCamera: (deviceId: string|null) => void
  selectedMicrophone: string|null, setSelectedMicrophone: (deviceId: string|null) => void
  selectedSpeaker: string|null, setSelectedSpeaker: (deviceId: string|null) => void
  speakersEnabled: boolean, setSpeakersEnabled: (enabled: boolean) => void
  selectedBackground: BackgroundFilterMode, setSelectedBackground: (mode: BackgroundFilterMode) => void
  permissions: DevicePermissions, setPermissions: (permissions: DevicePermissions) => void
  setCameraPermission: (allowed: boolean) => void, setMicrophonePermission: (allowed: boolean) => void
  // True when the last capture attempt failed (blocked, busy, or no device).
  cameraFailed: boolean
  micFailed: boolean
  // True while a camera capture is in flight (RISKS.md/P2.38). Re-enabling a camera in the OS takes
  // seconds, and neither "no camera" nor "camera off" is true then — without this the preview is
  // simply black with no explanation. The meeting-room tile refines it with its own frame state.
  cameraStarting: boolean
  // `cameraStarting` after the delay below — what the UI should actually render. Shared so the green
  // room and the meeting room cannot drift (RISKS.md/P2.28, P2.38).
  cameraStartingVisible: boolean
  // Unusable = ABSENT (empty list) or present-but-uncapturable (blocked, or held by another app).
  // Defined here so both rooms share one definition (RISKS.md/P2.28). Deliberately NOT keyed on
  // `permissions.*` (async, starts false → would flash a warning on every cold load) nor on
  // `videoEnabled`/`audioEnabled`: "off by choice" is not "unavailable" — a consumer wanting
  // "there is no picture" composes `!videoEnabled || cameraUnavailable` at its own call site.
  cameraUnavailable: boolean
  micUnavailable: boolean
  // Present and capturing, just silent — NOT unavailable. Folding it into `micUnavailable` empties
  // both device pickers and locks the mute toggle (RISKS.md/P2.41).
  micOsMuted: boolean
  // i18n key for the warning tooltip: the same copy as the snackbar, so a blocked, busy or muted
  // device is never described as a missing one (RISKS.md/P2.41).
  cameraWarningKey: string
  micWarningKey: string
  // Only the CHOICE is gone — blocking MICROPHONE access strips audio OUTPUT device ids too, but
  // playback still falls back to the system default. UI must read the system-default label,
  // never "None", which would deny a device the user can hear.
  speakerSelectionUnavailable: boolean
  // The DEVICE is gone, not merely the choice above. Debounced, so the label, the test button and
  // the toast cannot disagree (RISKS.md/P2.50).
  speakerDeviceMissing: boolean
  // Routes ONE element. Identity is stable for the provider's life, so it is safe in a dependency
  // array — this context re-renders ~20x/s while a microphone is live (RISKS.md/P2.39).
  applySinkId: (el: HTMLMediaElement | null) => void
  // Calling again restarts the tone rather than being ignored (RISKS.md/P2.39).
  testSpeaker: () => void
  speakerTesting: boolean
  // A ref, not state: the level bar polls it at 20Hz and must not re-render this context (P2.19).
  speakerTestEl: MutableRefObject<HTMLAudioElement | null>
  // Records the microphone and plays it back through the selected speaker. Calling again does NOT
  // cancel: mid-take it finishes and plays, mid-playback it stops (RISKS.md/P2.55).
  testMicrophone: () => void
  // The cancel `testMicrophone` is not — discards the take. Stable identity, safe in a dep array.
  cancelMicTest: () => void
  micTestState: MicTestState
  volumeMeter: VolumeMeter,
  volume: number, setVolume: (volume: number) => void
  loadDevices: () => void
  // Publishes whatever the local streams hold, and resolves either way — callers need no catch.
  publishLocalMedia: () => Promise<void>
}>({
  stream: new MediaStream(),
  videoStream: new MediaStream(),
  videoEnabled: true, setVideoEnabled: () => null,
  audioEnabled: true, setAudioEnabled: () => null,
  cameras: [], setCameras: () => null,
  microphones: [], setMicrophones: () => null,
  speakers: [], setSpeakers: () => null,
  selectedCamera: null, setSelectedCamera: () => null,
  selectedMicrophone: null, setSelectedMicrophone: () => null,
  selectedSpeaker: null, setSelectedSpeaker: () => null,
  speakersEnabled: true, setSpeakersEnabled: () => null,
  selectedBackground: 'none', setSelectedBackground: () => null,
  permissions: { microphone: false, camera: false }, setPermissions: () => null,
  setCameraPermission: () => null, setMicrophonePermission: () => null,
  cameraFailed: false,
  micFailed: false,
  cameraStarting: false,
  cameraStartingVisible: false,
  // All false: a component rendered outside the provider must not scream "no camera".
  cameraUnavailable: false,
  micUnavailable: false,
  micOsMuted: false,
  cameraWarningKey: 'lobby.cameraNotFound',
  micWarningKey: 'lobby.microphoneNotFound',
  speakerSelectionUnavailable: false,
  speakerDeviceMissing: false,
  applySinkId: () => null,
  testSpeaker: () => null,
  speakerTesting: false,
  speakerTestEl: { current: null },
  testMicrophone: () => null,
  cancelMicTest: () => null,
  micTestState: 'idle',
  volumeMeter: new VolumeMeter(),
  volume: 0, setVolume: () => null,
  loadDevices: () => null,
  publishLocalMedia: async () => {},
})

const MediaContextProvider = (props: Props) => {
  const webrtc = useWebRtcClient()
  const {t} = useTranslation()

  const {
    ws,
    currentRoom,
    checkedIn,
  } = useContext(AppContext)

  const {
    createNotification,
    hideNotification,
    clearGeneration,
  } = useContext(NotificationContext)

  const [ stream ] = useState<MediaStream>(new MediaStream())
  const [ videoStream ] = useState<MediaStream>(new MediaStream())

  // "A tone played through X" dies with X, and would otherwise stand beside the fallback toast
  // naming its replacement.
  const retractSpeakerTest = () => {
    hideNotification('speaker-test')
    hideNotification('speaker-test-failed')
  }

  // RISKS.md/P1.14: capture is serialized per device kind. The [selectedCamera] and [videoEnabled]
  // effects both fire on mount and both now reach getUserMedia, which would double-prompt. Chaining
  // also makes a rapid device switch last-write-wins instead of two interleaved track swaps.
  const cameraChain = useRef<Promise<void>>(Promise.resolve())
  const microphoneChain = useRef<Promise<void>>(Promise.resolve())
  const [volumeMeter] = useState<VolumeMeter>(new VolumeMeter())

  // RISKS.md/P2.2: pass values in. `useContext(MediaContext)` in a hook called here returns the
  // defaults, a `MediaStream` and a `VolumeMeter` nobody else holds, and still compiles.
  const {
    cameras, setCameras, microphones, setMicrophones, speakers, setSpeakers,
    selectedCamera, setSelectedCamera, setSelectedCameraState,
    selectedMicrophone, setSelectedMicrophone, setSelectedMicrophoneState,
    selectedSpeaker, setSelectedSpeaker, setSelectedSpeakerState,
    selectedSpeakerRef, speakersRef, recaptureAttemptsRef,
    loadDevices,
  } = useDeviceList({ retractSpeakerTest, clearGeneration, createNotification, hideNotification, t })

  const { publishLocalMedia, reportPublishFailure, clearPublishFailure } =
    usePublish({ stream, videoStream, webrtc, createNotification, hideNotification, t })

  const { selectedBackground, setSelectedBackground, applyFilterRef } =
    useBackgroundFilter({ stream, videoStream, cameraChain, webrtc, reportPublishFailure, clearPublishFailure })

  const [speakersEnabled, setSpeakersEnabled] = useState<boolean>(true)

  const [videoEnabled, setVideoEnabled] = useState(true)
  const [audioEnabled, setAudioEnabled] = useState(true)

  const [permissions, setPermissions] = useState({ microphone: false, camera: false })
  const [cameraFailed, setCameraFailed] = useState(false)
  const [micFailed, setMicFailed] = useState(false)
  const [micOsMuted, setMicOsMuted] = useState(false)
  const micOsMutedRef = useRef(false)
  micOsMutedRef.current = micOsMuted
  // i18n key of the LAST capture failure. Null until something fails, which is not the same as
  // `micUnavailable`: a cold load with nothing enumerated has no error to report (RISKS.md/P2.41).
  const [cameraFailKey, setCameraFailKey] = useState<string | null>(null)
  const [micFailKey, setMicFailKey] = useState<string | null>(null)
  // RISKS.md/P2.38: a COUNT of captures in flight, not a boolean. Two effects fire on mount
  // ([selectedCamera] and [videoEnabled]), so back-to-back captures made a boolean flip
  // true→false→true and the "starting" label appeared twice before the first frame.
  const [startingCaptures, setStartingCaptures] = useState(0)
  // Set the instant a capture is REQUESTED — before the coalesce window, before the chain runs.
  // Otherwise the "camera off" panel is already gone (it keys on `videoEnabled`, which flips during
  // render) with nothing to replace it, and a <video> keeps its last decoded frame: that gap shows a
  // flash of the previous picture (RISKS.md/P2.38).
  const [capturePending, setCapturePending] = useState(false)
  const cameraStarting = startingCaptures > 0 || capturePending
  const startCapture = () => setStartingCaptures(c => c + 1)
  const endCapture = () => setStartingCaptures(c => Math.max(0, c - 1))

  const [cameraStartingVisible, setCameraStartingVisible] = useState(false)
  const labelShownAtRef = useRef(0)
  useEffect(() => {
    if (cameraStarting) {
      // Already up: leave it alone, or re-running this effect would keep resetting the clock below.
      if (cameraStartingVisible) return
      const timer = setTimeout(() => {
        labelShownAtRef.current = Date.now()
        setCameraStartingVisible(true)
      }, cameraFailed ? RETRY_LABEL_MS : FRESH_LABEL_MS)
      return () => clearTimeout(timer)
    }
    if (!cameraStartingVisible) return
    // Capture finished. Hold the label for whatever is left of its minimum so it never flashes.
    const remaining = Math.max(0, MIN_LABEL_VISIBLE_MS - (Date.now() - labelShownAtRef.current))
    const timer = setTimeout(() => setCameraStartingVisible(false), remaining)
    return () => clearTimeout(timer)
  }, [cameraStarting, cameraFailed, cameraStartingVisible])

  const permissionsRef = useRef(permissions)
  permissionsRef.current = permissions

  // Counts capture REQUESTS per kind, so the `ended` handler can tell whether anything else already
  // asked for one while it was waiting out DEVICE_SETTLE_MS (RISKS.md/P2.38).
  const captureRequestsRef = useRef({ video: 0, audio: 0 })

  // Pending coalesce timer, so a burst of capture requests becomes one (RISKS.md/P2.38).
  const cameraCoalesceRef = useRef<any>(null)
  const microphoneCoalesceRef = useRef<any>(null)

  // RISKS.md/P1.14: a capture may wait on a permission prompt and on queued earlier requests, so it
  // re-checks intent once the stream is in hand — a slow grant must not resurrect a disabled device.
  const videoEnabledRef = useRef(videoEnabled)
  videoEnabledRef.current = videoEnabled

  const audioEnabledRef = useRef(audioEnabled)
  audioEnabledRef.current = audioEnabled

  // RISKS.md/P2.41: an OS mute surfaces ONLY as `track.muted` — getUserMedia succeeds, nothing
  // throws, the track stays `live` and the device stays enumerated, so there is no other signal.
  const applyMicOsMuted = (muted: boolean) => {
    setMicOsMuted(muted)
    if (!muted) {
      hideNotification('microphone-muted-system')
      return
    }
    createNotification({
      id: 'microphone-muted-system',
      message: t('lobby.microphoneMutedSystem'),
      persistent: true,
    })
  }

  // RISKS.md/P1.26: the cap was hit — the browser ended every fresh track. Called after markFailed
  // and the retraction; what is missing is the toast, because the last capture SUCCEEDED and cleared
  // it. Same id as the capture failure, so the success path retracts it; hidden first (P2.44).
  const giveUpRecapture = (kind: 'video' | 'audio') => {
    const id = kind === 'video' ? 'camera-unavailable' : 'microphone-unavailable'
    const key = kind === 'video' ? 'lobby.cameraLost' : 'lobby.microphoneLost'
    const retry = kind === 'video' ? enableCameraRef : enableMicrophoneRef
    if (kind === 'video') setCameraFailKey(key)
    else setMicFailKey(key)
    hideNotification(id)
    createNotification({
      id,
      message: t(key),
      persistent: true,
      actions: [{ label: t('lobby.retry'), onClick: () => { void loadDevices().then(() => retry.current('user')) } }],
    })
  }

  // RISKS.md/P2.34: watch a local track for the browser killing it. Nothing observed this before,
  // so revoking access mid-session left both the local flags and the published presence claiming
  // the device still worked. `ended` is terminal (revoked permission); `mute` is not — the OS can
  // mute a device temporarily — so it does NOT retract the producer, letting an `unmute` restore
  // things without a renegotiation.
  const watchTrack = (track: MediaStreamTrack, kind: 'video' | 'audio') => {
    const markFailed = (failed: boolean) => {
      if (kind === 'video') setCameraFailed(failed)
      else setMicFailed(failed)
    }
    const bornAt = Date.now()
    // Only the track still in `stream` may speak for the device: a superseding capture removes it
    // there first, so a late event from a discarded track would report on the wrong one
    // (RISKS.md/P2.41) — or, for `ended`, retract and re-capture on its behalf (RISKS.md/P1.26).
    const speaksForDevice = () =>
      (kind === 'video' ? stream.getVideoTracks() : stream.getAudioTracks()).includes(track)

    track.addEventListener('ended', () => {
      if (!speaksForDevice()) return
      console.warn(`local ${kind} track ended`)
      markFailed(true)

      // Drop the corpse. Nothing else removes an ended track from `stream`, and everything
      // downstream ("do I have a picture", "what do I publish") asks that question of `stream`.
      const dead = kind === 'video' ? stream.getVideoTracks() : stream.getAudioTracks()
      dead.filter(t => t.readyState === 'ended').forEach(t => stream.removeTrack(t))

      // RISKS.md/P2.34: tear the pipeline down too — retracting alone was NOT enough. The filter's
      // canvas track does not die with the camera (a canvas produces frames forever), so it stayed
      // live in `videoStream` and the next `createSender` republished it, pointing peers at a
      // producer carrying a frozen frame. applyFilter's empty-input path clears `videoStream` and
      // stops the filter, leaving nothing to republish.
      if (kind === 'video') {
        enqueue(cameraChain, 'filter after track end', () => applyFilterRef.current())
      }

      // Tell everyone else: the published status is a producer id, and its ABSENCE is the only way
      // to say "off". Without this the peer keeps rendering us as camera-on forever.
      if (webrtc.isInitialized()) {
        webrtc.disableTrack('camera', kind).catch(e => console.warn('disableTrack failed:', e))
      }

      // RISKS.md/P2.38: re-attempt capture. An ended track is the ONLY signal for a device the OS
      // disabled WITHOUT removing it from enumerateDevices() — Windows does that for audio inputs,
      // where nothing re-selects and getUserMedia would never run again. Re-running it either
      // recovers the device or produces the error that raises the snackbar. DELAYED because firing
      // immediately races the OS teardown, which answers `NotReadableError` — the user would
      // otherwise see "in use by another application" before the correct "not found".
      // RISKS.md/P1.26: `stop()` fires no `ended`, but the browser ending each FRESH track does loop —
      // every retry attaches this listener again — so the retries are counted, backed off and capped.
      if (Date.now() - bornAt >= RECAPTURE_STABLE_MS) recaptureAttemptsRef.current[kind] = 0
      const attempt = recaptureAttemptsRef.current[kind]
      if (attempt >= RECAPTURE_MAX_ATTEMPTS) {
        giveUpRecapture(kind)
        return
      }
      recaptureAttemptsRef.current[kind] = attempt + 1
      const requestsWhenEnded = captureRequestsRef.current[kind]
      setTimeout(() => {
        // Skip if something already asked for a capture meanwhile. Where the OS REMOVES the device
        // the normal effects fire on their own and this would be a redundant second getUserMedia;
        // where it leaves the device enumerated, this is the only attempt there is.
        if (captureRequestsRef.current[kind] !== requestsWhenEnded) return
        if (kind === 'video') {
          enableCameraRef.current()
        } else {
          enableMicrophoneRef.current()
        }
      }, DEVICE_SETTLE_MS * 2 ** attempt)
    })
    const markMuted = (muted: boolean) => {
      if (!speaksForDevice()) return
      if (kind === 'video') markFailed(muted)
      else applyMicOsMuted(muted)
    }

    track.addEventListener('mute', () => markMuted(true))
    track.addEventListener('unmute', () => markMuted(false))
  }

  // RISKS.md/P2.36: the retry buttons on the persistent "device in use" toasts call through these.
  // The toast is created in one render and clicked in a later one, so a captured closure would
  // retry with whatever device was selected when the failure happened. Reassigned every render.
  const enableCameraRef = useRef<(origin?: 'user') => void>(() => {})
  const enableMicrophoneRef = useRef<(origin?: 'user') => void>(() => {})

  // RISKS.md/P1.14: do NOT gate this on navigator.permissions.query — getUserMedia's own
  // success/failure IS the permission signal. A first visit reads 'prompt', and Safari/Firefox
  // reject those query names outright, so a gate leaves a black green room with no error.
  // `'user'` = a capture the user asked for (toggle, toast retry): it resets the re-capture cap. The
  // `[selectedX]` effects must not — they re-fire off the very device-list flap the loop lives in
  // (RISKS.md/P1.26). A device pick resets it in its setter instead.
  const enableCamera = (origin?: 'user') => {
    if (origin === 'user') recaptureAttemptsRef.current.video = 0
    // RISKS.md/P2.38: coalesce FIRST, then queue. Mount fires three of these within ~110ms, and the
    // promise chain cannot merge them — it is empty at that point, so the first two execute before
    // the third is even requested and a check from inside the chain has nothing to defer to. Each
    // call restarts this timer, making the burst one capture instead of three.
    const seq = ++captureRequestsRef.current.video
    setCapturePending(true)
    if (cameraCoalesceRef.current) clearTimeout(cameraCoalesceRef.current)
    cameraCoalesceRef.current = setTimeout(() => {
      cameraCoalesceRef.current = null
      // Hand over to the counter in the same tick, so React batches and the flag never gaps.
      startCapture()
      setCapturePending(false)
      queueCameraCapture(seq)
    }, CAPTURE_COALESCE_MS)
  }

  const queueCameraCapture = (seq: number) => {
    // RISKS.md/P1.24: guarded, because the release path calls it too — a second decrement would
    // clear a later capture's "starting" state.
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      endCapture()
    }
    enqueue(cameraChain, 'camera capture', async fence => {
      if (captureRequestsRef.current.video !== seq) {
        // A newer request is queued behind this one; let it do the work.
        finish()
        return
      }
      let camera: MediaStream
      let camFellBack = false
      try {
        camera = await fence.unclocked(async () => {
          // NOT gated on a known deviceId: browsers report an EMPTY deviceId until access is
          // granted, so requiring one meant the very first load never asked.
          if (!selectedCamera) return navigator.mediaDevices.getUserMedia({ video: true })
          try {
            // Never a bare deviceId — the browser may silently swap it (RISKS.md/P2.52).
            return await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: selectedCamera } } })
          } catch (e: any) {
            if (e?.name !== 'OverconstrainedError') throw e
            const fallback = await navigator.mediaDevices.getUserMedia({ video: true })
            camFellBack = true
            return fallback
          }
        })
      } catch (e: any) {
        console.warn('Camera not available:', e)
        // Before the retraction below, which can wait out a whole reconnect: the preview must not
        // claim the camera is starting for that long.
        finish()
        // Blocked, busy or absent all leave a black preview — the green room labels it instead.
        setCameraFailed(true)
        // Drop tracks the browser has ALREADY ended, or the <video> keeps painting a dead frame over
        // the label. Only `ended` ones — a busy-camera failure can leave the previous track working.
        stream.getVideoTracks().filter(t => t.readyState === 'ended').forEach(t => stream.removeTrack(t))
        videoStream.getVideoTracks().filter(t => t.readyState === 'ended').forEach(t => videoStream.removeTrack(t))
        // A denial is a permission fact; a busy/failing device leaves the permission untouched.
        // Fixed toast ids so retries replace rather than stack.
        if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
          setCameraPermission(false)
          // RISKS.md/P2.27: an OS-level block arrives as the SAME NotAllowedError, so the generic
          // copy sent the user to a browser permission that is already granted.
          // Resolved once and reused for the tooltip, so the two cannot disagree (RISKS.md/P2.41).
          const key = isSystemDenial(e) ? 'lobby.cameraDeniedSystem' : 'lobby.cameraDenied'
          setCameraFailKey(key)
          createNotification({
            id: 'camera-denied',
            message: t(key),
            persistent: true,
            // An OS block leaves enumeration unchanged, so nothing re-fires on its own — a fresh
            // getUserMedia call is the only thing that recovers the grant (RISKS.md/P2.43).
            actions: [{ label: t('lobby.retry'), onClick: () => { void loadDevices().then(() => enableCameraRef.current('user')) } }],
          })
        } else {
          // RISKS.md/P2.36: a busy device is the one failure nothing recovers from on its own — a
          // denial restores the deviceIds and re-runs capture, but a busy camera keeps its real id,
          // so no effect re-fires and the green room's button is inert while `cameraUnavailable`.
          // Hence persistent + a retry action. Transient errors keep the 8s toast.
          const busy = e?.name === 'NotReadableError' || e?.name === 'TrackStartError'
          // RISKS.md/P2.38: NotFoundError is toasted too. It used to be swallowed because "no camera
          // attached" has its own green-room label — true for a machine without a webcam, wrong for
          // one the user just disabled in the OS, who can fix it and is told nothing. The two are
          // indistinguishable here (both vanish from enumerateDevices), so one message covers both.
          const notFound = e?.name === 'NotFoundError' || e?.name === 'DevicesNotFoundError'
          const key = notFound ? 'lobby.cameraNotFound' : 'lobby.cameraUnavailable'
          setCameraFailKey(key)
          createNotification({
            id: 'camera-unavailable',
            message: t(key),
            // Retry re-enumerates AND re-captures, in that order — neither alone works. Without
            // loadDevices the list stays empty, so `cameraUnavailable` holds and the `lobby.noCamera`
            // label sticks even on success. Without the capture nothing re-raises the toast, which
            // the action click has just dismissed, leaving the user with nothing (RISKS.md/P2.38).
            ...(notFound || busy
              ? {
                persistent: true,
                actions: [{
                  label: t('lobby.retry'),
                  onClick: () => { void loadDevices().then(() => enableCameraRef.current('user')) },
                }],
              }
              : { ttl: DEVICE_TOAST_TTL_MS }),
          })
        }
        // RISKS.md/P2.33: retract, exactly as switching the camera off does. The roster field peers
        // read is a mediasoup PRODUCER ID and this call is the only thing that clears it — without
        // it they keep consuming a dead producer (the `producer not found` console spam).
        if (webrtc.isInitialized()) {
          await webrtc.disableTrack('camera', 'video').catch(err => console.warn('disableTrack failed:', err))
        }
        return
      }

      setCameraPermission(true)
      setCameraFailed(false)
      setCameraFailKey(null)
      hideNotification('camera-denied')
      // RISKS.md/P2.36: retract it, or the persistent variant outlives the condition.
      hideNotification('camera-unavailable')

      if (camFellBack) {
        // From the TRACK, never the device list: this closure can be a render behind holding an
        // EMPTY list, which silently blanks the name and skips the re-point (RISKS.md/P2.44).
        const track = camera.getVideoTracks()[0]
        const actualId = track?.getSettings().deviceId ?? null
        const name = stripLabel(track?.label ?? '') || t('lobby.systemDefault')
        // De-dupe is FIRST-wins: without this hide, an earlier worse-named toast wins (RISKS.md/P2.44).
        hideNotification('camera-fallback')
        createNotification({
          id: 'camera-fallback',
          message: t('lobby.cameraFallback', { name }),
          ttl: DEVICE_TOAST_TTL_MS,
        })
        if (actualId) setSelectedCameraState(actualId)
      }

      if (!videoEnabledRef.current) {
        // Turned off while this request waited on the prompt or an earlier request — honour that.
        finish()
        camera.getTracks().forEach(t => t.stop())
        return
      }

      stream.getVideoTracks().forEach(t => {
        stream.removeTrack(t)
        t.stop()
      })

      camera.getVideoTracks().forEach(t => {
        watchTrack(t, 'video')
        stream.addTrack(t)
      })

      // RISKS.md/P2.29: this writes `stream` (raw capture) only — `videoStream` and the outgoing
      // producer belong to applyFilter and NOWHERE else. Do not install or publish the raw track
      // here: React cannot see a MediaStream mutation, so the filter would never be applied.
      await applyFilterRef.current()
      finish()
    }, finish)
  }

  // Mirrors enableCamera above (RISKS.md/P1.14).
  const enableMicrophone = (origin?: 'user') => {
    if (origin === 'user') recaptureAttemptsRef.current.audio = 0
    // Coalesced exactly like the camera (RISKS.md/P2.38). Disabling an input device makes the list
    // flap (4 → 3 → 0), and each step re-selected and re-captured, so one disable produced a run of
    // identical NotFoundErrors. Harmless — they share a toast id — but the log was unreadable.
    const seq = ++captureRequestsRef.current.audio
    if (microphoneCoalesceRef.current) clearTimeout(microphoneCoalesceRef.current)
    microphoneCoalesceRef.current = setTimeout(() => {
      microphoneCoalesceRef.current = null
      queueMicrophoneCapture(seq)
    }, CAPTURE_COALESCE_MS)
  }

  const queueMicrophoneCapture = (seq: number) => {
    enqueue(microphoneChain, 'microphone capture', async fence => {
      if (captureRequestsRef.current.audio !== seq) return
      let microphone: MediaStream
      let fellBack = false
      try {
        microphone = await fence.unclocked(async () => {
          if (!selectedMicrophone) return navigator.mediaDevices.getUserMedia({ audio: true })
          try {
            // Never a bare deviceId — the browser may silently swap it (RISKS.md/P2.52).
            return await navigator.mediaDevices.getUserMedia({
              audio: { deviceId: { exact: selectedMicrophone } },
            })
          } catch (e: any) {
            if (e?.name !== 'OverconstrainedError') throw e
            const fallback = await navigator.mediaDevices.getUserMedia({ audio: true })
            fellBack = true
            return fallback
          }
        })
      } catch (e: any) {
        console.warn('Microphone not available:', e)
        setMicFailed(true)
        applyMicOsMuted(false)
        // Mirror of the camera path: drop already-ended tracks so nothing downstream — the volume
        // meter, or produce() at join — is handed a dead one (RISKS.md/P1.23).
        stream.getAudioTracks().filter(t => t.readyState === 'ended').forEach(t => stream.removeTrack(t))
        if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
          setMicrophonePermission(false)
          // RISKS.md/P2.27: as above — an OS-level block needs system-settings copy.
          const key = isSystemDenial(e) ? 'lobby.microphoneDeniedSystem' : 'lobby.microphoneDenied'
          setMicFailKey(key)
          createNotification({
            id: 'microphone-denied',
            message: t(key),
            persistent: true,
            actions: [{ label: t('lobby.retry'), onClick: () => { void loadDevices().then(() => enableMicrophoneRef.current('user')) } }],
          })
        } else {
          // Mirror of the camera path (RISKS.md/P2.36, P2.38) — nothing retries a busy device on its
          // own, and a mic disabled in the OS is told apart from a missing one by nobody.
          const busy = e?.name === 'NotReadableError' || e?.name === 'TrackStartError'
          const notFound = e?.name === 'NotFoundError' || e?.name === 'DevicesNotFoundError'
          const key = notFound ? 'lobby.microphoneNotFound' : 'lobby.microphoneUnavailable'
          setMicFailKey(key)
          createNotification({
            id: 'microphone-unavailable',
            message: t(key),
            // Same as the camera path: re-enumerate, then re-capture so a still-missing device
            // re-raises this toast, which the action click has just dismissed (RISKS.md/P2.38).
            ...(notFound || busy
              ? {
                persistent: true,
                actions: [{
                  label: t('lobby.retry'),
                  onClick: () => { void loadDevices().then(() => enableMicrophoneRef.current('user')) },
                }],
              }
              : { ttl: DEVICE_TOAST_TTL_MS }),
          })
        }
        // Same retraction as the camera path (RISKS.md/P2.33), or peers keep consuming a dead
        // audio producer and keep showing us as un-muted.
        if (webrtc.isInitialized()) {
          await webrtc.disableTrack('camera', 'audio').catch(err => console.warn('disableTrack failed:', err))
        }
        return
      }

      setMicrophonePermission(true)
      setMicFailed(false)
      setMicFailKey(null)
      // Cleared here, re-asserted from the new track below: a switch between a muted and a working
      // microphone must not leave the previous device's verdict standing (RISKS.md/P2.41).
      applyMicOsMuted(false)
      hideNotification('microphone-denied')
      hideNotification('microphone-unavailable')

      if (fellBack) {
        // From the TRACK, never the device list: this closure can be a render behind holding an
        // EMPTY list, which silently blanks the name and skips the re-point (RISKS.md/P2.44).
        const track = microphone.getAudioTracks()[0]
        const actualId = track?.getSettings().deviceId ?? null
        const name = stripLabel(track?.label ?? '') || t('lobby.systemDefault')
        // De-dupe is FIRST-wins: without this hide, an earlier worse-named toast wins (RISKS.md/P2.44).
        hideNotification('microphone-fallback')
        createNotification({
          id: 'microphone-fallback',
          message: t('lobby.microphoneFallback', { name }),
          ttl: DEVICE_TOAST_TTL_MS,
        })
        if (actualId) setSelectedMicrophoneState(actualId)
      }

      if (!audioEnabledRef.current) {
        microphone.getTracks().forEach(t => t.stop())
        return
      }

      stream.getAudioTracks().forEach(t => {
        stream.removeTrack(t)
        t.stop()
      })
      microphone.getAudioTracks().forEach(t => {
        watchTrack(t, 'audio')
        stream.addTrack(t)
      })

      // RISKS.md/P2.41: a track captured from an already-muted mic is born muted and no event ever
      // follows — the listeners above see state CHANGES only. The common case, not the rare one:
      // the app's own mute STOPS the track, so every re-enable lands here.
      applyMicOsMuted(stream.getAudioTracks()[0]?.muted === true)

      volumeMeter.setStream(stream)

      if (webrtc.isInitialized()) {
        // RISKS.md/P2.15: report, don't reject — the capture succeeded and the microphone stays live.
        try {
          if (await webrtc.enableTrack(stream.getAudioTracks()[0], 'camera')) clearPublishFailure(['audio'])
        } catch (e) {
          reportPublishFailure(['audio'], e)
        }
      }
    })
  }

  // Kept current so the toast retry buttons always call this render's closures (RISKS.md/P2.36).
  enableCameraRef.current = enableCamera
  enableMicrophoneRef.current = enableMicrophone

  const disableCamera = async () => {
    stream.getVideoTracks().forEach(t => {
      t.stop()
      stream.removeTrack(t)
    })
    // RISKS.md/P2.29: clears the PREVIEW and stops the filter. Emptying `stream` alone left the
    // canvas painting a live, frozen picture of the user into `videoStream` after they turned off.
    await applyFilterRef.current()
    if (webrtc.isInitialized()) {
      await webrtc.disableTrack('camera', 'video')
    }
  }

  const disableMicrophone = async () => {
    stream.getAudioTracks().forEach(t => {
      t.stop()
      stream.removeTrack(t)
    })
    // With no track there is nothing to be silent, and `stop()` fires no `unmute` to clear it — a
    // mic switched off in the app must not go on warning about the OS (RISKS.md/P2.41).
    applyMicOsMuted(false)
    if (webrtc.isInitialized()) {
      await webrtc.disableTrack('camera', 'audio')
    }
  }

  useEffect(() => {
    enableCamera()
  }, [selectedCamera]);

  useEffect(() => { enableMicrophone() }, [selectedMicrophone]);


  useEffect(() => {
    document.querySelectorAll('video').forEach(el => {
      const htmlEl = el as HTMLVideoElement
      if (!htmlEl.dataset.local) {
        htmlEl.muted = !speakersEnabled
      }
    })
  }, [speakersEnabled])

  const {
    applySinkIdRef, applySinkId, speakerSelectionBlocked, speakerDeviceMissing,
    testSpeaker, speakerTesting, speakerTestElRef,
  } = useSpeakerOutput({
    selectedSpeaker, selectedSpeakerRef, setSelectedSpeakerState, speakers, microphones, cameras,
    loadDevices, retractSpeakerTest, micFailKey, clearGeneration, createNotification, hideNotification, t,
  })

  const cameraUnavailable = cameras.length === 0 || cameraFailed
  const micUnavailable = microphones.length === 0 || micFailed
  const speakerSelectionUnavailable = speakers.length === 0 || speakers.every(s => !s.deviceId) || speakerSelectionBlocked

  const { testMicrophone, cancelMicTest, micTestState } = useMicrophoneTest({
    stream, volumeMeter, applySinkIdRef, speakersRef, selectedSpeakerRef, checkedIn,
    audioEnabledRef, micOsMutedRef, micUnavailable, retractSpeakerTest, createNotification, hideNotification, t,
  })

  useEffect(() => {
    if (!videoEnabled) {
      // RISKS.md/P2.29: same queue as enableCamera — both write `videoStream` through applyFilter,
      // so an unchained disable could interleave with a capture still in flight.
      enqueue(cameraChain, 'camera disable', () => disableCamera())
    } else {
      enableCamera('user')
    }
  }, [videoEnabled])

  useEffect(() => {
    if (!audioEnabled) {
      void disableMicrophone().catch(e => console.warn('disableMicrophone failed:', e))
    } else {
      enableMicrophone('user')
    }
    // @ts-ignore
    ws?.sendRoomAction(currentRoom as string, 'mute', !audioEnabled)
  }, [audioEnabled])

  useEffect(() => {
  }, [permissions]);

  // RISKS.md/P1.14: camera and mic can settle in the same tick, and a `{...permissionsRef.current}`
  // spread reads a ref that only refreshes on render — so the second write would drop the first.
  const setCameraPermission = (allowed: boolean) => {
    setPermissions(prev => ({
      ...prev,
      camera: allowed,
    }))
  }

  const setMicrophonePermission = (allowed: boolean) => {
    setPermissions(prev => ({
      ...prev,
      microphone: allowed,
    }))
  }

  const { volume, setVolume } = useMicLevel(volumeMeter, ws)

  // Resolved here, not at the call sites, so the two rooms cannot drift (RISKS.md/P2.28). An OS mute
  // outranks a stale failure key; the fallback is the not-found copy because reaching it means the
  // list is empty with nothing yet failed, which is what that copy says (RISKS.md/P2.41).
  const cameraWarningKey = cameraFailKey ?? 'lobby.cameraNotFound'
  const micWarningKey = micOsMuted ? 'lobby.microphoneMutedSystem' : (micFailKey ?? 'lobby.microphoneNotFound')

  const value = {
    stream,
    videoStream,
    cameras, setCameras,
    microphones, setMicrophones,
    speakers, setSpeakers,
    selectedCamera, setSelectedCamera,
    selectedMicrophone, setSelectedMicrophone,
    selectedSpeaker, setSelectedSpeaker,
    speakersEnabled, setSpeakersEnabled,
    selectedBackground, setSelectedBackground,
    volumeMeter,
    volume, setVolume,
    videoEnabled, setVideoEnabled,
    audioEnabled, setAudioEnabled,
    permissions, setPermissions,
    setCameraPermission, setMicrophonePermission,
    cameraFailed, micFailed, cameraStarting, cameraStartingVisible,
    cameraUnavailable, micUnavailable, micOsMuted, speakerSelectionUnavailable,
    cameraWarningKey, micWarningKey,
    speakerDeviceMissing,
    applySinkId, testSpeaker, speakerTesting, speakerTestEl: speakerTestElRef,
    testMicrophone, cancelMicTest, micTestState,
    loadDevices,
    publishLocalMedia,
  }

  return (
    <MediaContext.Provider value={value}>
      {props.children}
    </MediaContext.Provider>
  )
}

export {
  MediaContext as default,
  MediaContextProvider,
}